import {
	Box,
	Button,
	CircularProgress,
	Container,
	IconButton,
	Paper,
	Skeleton,
	Table,
	TableBody,
	TableContainer,
	Typography,
} from '@mui/material';
import moment from 'moment-timezone';

import { FC, ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getMerchantId } from 'redux/reducers/auth/selectors';
import { getEmAssignmentRequest } from 'redux/reducers/settings/reducer';
import { getEmAssignment, getEmAssignmentLoader } from 'redux/reducers/settings/selectors';
import { TEmAssignmentResponse } from 'redux/reducers/settings/types';
import { StyledTableCell, StyledTableRow } from 'ui/StyledTable';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { nanoid } from '@reduxjs/toolkit';
import { getUserInfo } from 'redux/reducers/user/selectors';
import CancelIcon from '@mui/icons-material/Cancel';
import { EditSettingDialog } from './EditSettingDialog';
import { SpecialSchemesRegistrationInformation } from './SpecialSchemesRegistrationInformation';
import {
	EAS_FEE_CALCULATION_OPTIONS,
	GENERAL_SETTINGS_ROWS,
	IOSS_SALE_OVER_THRESHOLD_OPTIONS,
	PRODUCT_LANGUAGE_OPTIONS,
	TEditableSetting,
	TGeneralSettingRow,
} from './constants';

const ROWS = Array.from(new Array(4));

const CELL_SX = {
	padding: '8px 8px 8px 8px',
	verticalAlign: 'middle',
	lineHeight: '1rem',
	maxWidth: '250px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	position: 'relative',
	m: '0px',
};

const findOptionLabel = (options: string[][], value: unknown) =>
	options.find((el) => el[0].toLowerCase() === String(value).toLowerCase())?.[1] ??
	String(value ?? '');

export const GeneralSettings: FC = () => {
	const dispatch = useDispatch();
	const emAssignment = useSelector(getEmAssignment);
	const emAssignmentIsLoading = useSelector(getEmAssignmentLoader);
	const xMerchantId = useSelector(getMerchantId);
	const userInfo = useSelector(getUserInfo);

	const [visibility, setVisibility] = useState(false);
	const [editSetting, setEditSetting] = useState<TEditableSetting | null>(null);
	const handleCloseEditDialog = () => {
		setEditSetting(null);
	};

	useEffect(() => {
		dispatch(getEmAssignmentRequest());
	}, [dispatch]);

	const handleCancellationRequest = () => {
		if (!emAssignment) return;
		if (!userInfo) return;
		window.open(
			`https://form.jotform.com/253202901852046?q3_textbox1=${emAssignment?.id}&company=${emAssignment?.fullName}&name=${userInfo.name}&q4_email2=${userInfo.email} `,
			'_blank',
		);
	};

	const renderValue = (row: TGeneralSettingRow, data: TEmAssignmentResponse): ReactNode => {
		const value = data[row.name];
		if (row.secret && !visibility) return '********';

		switch (row.name) {
			case 'timezone':
				return (
					<div>
						{data.timezone}{' '}
						<span style={{ color: 'gray' }}>
							{data.timezone ? moment().tz(data.timezone).format('Z') : ''}
						</span>
					</div>
				);
			case 'easFeeIncludedInLandedCost':
				return findOptionLabel(EAS_FEE_CALCULATION_OPTIONS, value);
			case 'productLanguage':
				return findOptionLabel(PRODUCT_LANGUAGE_OPTIONS, value);
			case 'iossSaleOverThreshold':
				return findOptionLabel(IOSS_SALE_OVER_THRESHOLD_OPTIONS, value);
			default:
				return value as ReactNode;
		}
	};

	return (
		<Container>
			<Paper
				sx={{
					margin: '0px 10px 0 10px',
					maxWidth: 'calc(100vw - 20px)',
					overflowX: 'auto',
				}}
			>
				<Box sx={{ width: 'auto', minWidth: '650px' }}>
					<Box sx={{ borderRadius: '12px' }}>
						<Box
							sx={{
								backgroundColor: 'primary.light',
								p: 0.5,
							}}
						>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									p: '2px 4px',
								}}
							>
								<Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
									<Typography
										sx={{ fontSize: '1.2rem', color: 'primary.main', fontWeight: 'bold' }}
									>
										General Settings
									</Typography>

									<Typography sx={{ fontSize: '1rem', color: 'primary.main', fontWeight: 'bold' }}>
										Merchant #{xMerchantId}
									</Typography>
								</Box>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row' }}>
									{emAssignmentIsLoading && <CircularProgress size={20} />}

									<Button
										variant="contained"
										color="warning"
										size="small"
										sx={{
											fontWeight: 'bold',
										}}
										onClick={handleCancellationRequest}
										startIcon={<CancelIcon />}
									>
										Cancellation Request
									</Button>
								</Box>
							</Box>
						</Box>
						{!emAssignmentIsLoading && emAssignment && (
							<TableContainer>
								<Table stickyHeader aria-label="sticky table">
									<TableBody>
										{GENERAL_SETTINGS_ROWS.map((row) => (
											<StyledTableRow
												key={row.name}
												sx={{
													'&:last-child td, &:last-child th': { border: 0 },
													background: 'auto',
													p: '0px',
													m: '0px',
													height: '35px !important',
												}}
											>
												<StyledTableCell align="left" colSpan={1} sx={CELL_SX} title={row.title}>
													{row.title}
												</StyledTableCell>
												<StyledTableCell align="left" colSpan={2} sx={CELL_SX}>
													{renderValue(row, emAssignment)}
												</StyledTableCell>
												<StyledTableCell align="right" colSpan={2} sx={CELL_SX}>
													{row.secret && (
														<IconButton
															onClick={() => setVisibility(!visibility)}
															size="small"
															sx={{ p: 0, m: 0 }}
														>
															{visibility ? (
																<VisibilityOffIcon
																	sx={{ color: 'primary.main', fontSize: '1.2rem', p: 0, m: 0 }}
																/>
															) : (
																<VisibilityIcon
																	sx={{ color: 'primary.main', fontSize: '1.2rem', p: 0, m: 0 }}
																/>
															)}
														</IconButton>
													)}

													{row.editable && (
														<IconButton
															onClick={() => setEditSetting(row.editable ?? null)}
															size="small"
															sx={{ p: 0, m: 0 }}
														>
															<EditIcon
																sx={{
																	fontSize: '1.2rem',
																	p: 0,
																	m: 0,
																	width: '1.2rem',
																	verticalAlign: 'middle',
																	color: 'primary.main',
																}}
															/>
														</IconButton>
													)}
												</StyledTableCell>
											</StyledTableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
						{emAssignmentIsLoading && (
							<Box
								sx={{
									pb: '1px',
								}}
							>
								{ROWS.map(() => (
									<Skeleton
										key={nanoid()}
										variant="rectangular"
										height="35px"
										sx={{
											mb: '35px',
										}}
									/>
								))}
							</Box>
						)}
					</Box>
				</Box>
			</Paper>
			<SpecialSchemesRegistrationInformation />

			<EditSettingDialog setting={editSetting} setClose={handleCloseEditDialog} />
		</Container>
	);
};
